import { useState, useEffect } from "react";
import { useApp } from "@/contexts/AppContext";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Shield, CheckCircle, AlertTriangle, AlertCircle, TrendingUp, Lock, ClipboardList, ArrowRight, Zap } from "lucide-react";

function ScoreRing({ score, grade, size = 160 }) {
  const radius = (size - 16) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  const color = score >= 85 ? "#065F46" : score >= 70 ? "#C8A961" : score >= 55 ? "#92400E" : "#991B1B";

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size/2} cy={size/2} r={radius} stroke="#E8E2D5" strokeWidth="8" fill="none" />
        <circle cx={size/2} cy={size/2} r={radius} stroke={color} strokeWidth="8" fill="none"
          strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round"
          style={{ transition: "stroke-dashoffset 1s ease-out" }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-mono text-4xl font-bold tabular-nums" style={{ color }}>{score}</span>
        <span className="text-sm font-medium text-[#6B7280]">/ 100</span>
        <span className="font-display text-lg font-semibold mt-1" style={{ color }}>{grade}</span>
      </div>
    </div>
  );
}

function ComponentBar({ label, score, max, detail, icon: Icon }) {
  const pct = (score / max) * 100;
  const color = pct >= 80 ? "#065F46" : pct >= 60 ? "#C8A961" : pct >= 40 ? "#92400E" : "#991B1B";
  return (
    <div className="stratum-card p-4">
      <div className="flex items-center gap-2 mb-2">
        <Icon size={16} className="text-[#C8A961]" />
        <span className="text-sm font-medium text-[#0B1020]">{label}</span>
        <span className="ml-auto font-mono text-sm tabular-nums font-semibold" style={{ color }}>{score}/{max}</span>
      </div>
      <div className="h-2 bg-[#E8E2D5] rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all duration-1000" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
      <p className="text-xs text-[#6B7280] mt-1.5">{detail}</p>
    </div>
  );
}

export default function HealthScorePage() {
  const { activeEntity, entities } = useApp();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [entityFilter, setEntityFilter] = useState("");
  const [fixing, setFixing] = useState(false);

  const canFix = ["system_admin", "asset_manager"].includes(user?.role);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (entityFilter) params.entity_id = entityFilter;
      const { data: d } = await api.get("/health-score", { params });
      setData(d);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [entityFilter]);

  const quickFix = async (action) => {
    const msgs = {
      lock_all_past_periods: "Lock all past periods with journal entries? This prevents backdated changes.",
      fix_anomalies: "Auto-fix all critical anomalies (over-depreciation, carrying mismatches)?"
    };
    if (!window.confirm(msgs[action] || "Apply this fix?")) return;
    setFixing(true);
    try {
      const { data: d } = await api.post("/health-score/quick-fix", { action });
      alert(d.results?.join("\n") || d.message || "Done");
      load(); // Refresh score
    } catch (e) { alert(e.response?.data?.detail || "Error"); }
    finally { setFixing(false); }
  };

  return (
    <div className="space-y-6" data-testid="health-score-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold text-[#0B1020]">
            <Shield size={24} className="inline mr-2 text-[#C8A961]" />
            Compliance Health Score
          </h1>
          <p className="text-sm text-[#6B7280] mt-1">Combined score across data integrity, depreciation reconciliation, period controls, and policy review</p>
        </div>
        <select className="stratum-input" value={entityFilter} onChange={(e) => setEntityFilter(e.target.value)} data-testid="health-entity-filter">
          <option value="">All Entities</option>
          {entities.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
        </select>
      </div>

      {loading || !data ? (
        <div className="stratum-card p-12 text-center text-[#6B7280]">Computing health score...</div>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Score Ring */}
            <div className="stratum-card p-8 flex flex-col items-center justify-center">
              <ScoreRing score={data.total_score} grade={data.grade} />
              <p className="text-sm text-[#6B7280] mt-4">{data.asset_count} assets under management</p>
            </div>

            {/* Components */}
            <div className="lg:col-span-2 space-y-3">
              <ComponentBar label={data.components.anomaly.label} score={data.components.anomaly.score} max={data.components.anomaly.max} detail={data.components.anomaly.detail} icon={AlertCircle} />
              <ComponentBar label={data.components.recon.label} score={data.components.recon.score} max={data.components.recon.max} detail={data.components.recon.detail} icon={CheckCircle} />
              <ComponentBar label={data.components.period.label} score={data.components.period.score} max={data.components.period.max} detail={data.components.period.detail} icon={Lock} />
              <ComponentBar label={data.components.policy.label} score={data.components.policy.score} max={data.components.policy.max} detail={data.components.policy.detail} icon={ClipboardList} />
            </div>
          </div>

          {/* Recommendations */}
          {data.recommendations.length > 0 && (
            <div className="stratum-card p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-display text-lg font-semibold text-[#0B1020]">Recommendations to Improve Score</h3>
                {canFix && (
                  <div className="flex gap-2">
                    <button onClick={() => quickFix("lock_all_past_periods")} disabled={fixing} className="px-3 py-1.5 text-xs rounded-lg bg-[#0B1020] text-white hover:bg-[#1A233A] flex items-center gap-1.5" data-testid="quick-lock-btn">
                      <Lock size={12} /> Lock All Past Periods
                    </button>
                    <button onClick={() => quickFix("fix_anomalies")} disabled={fixing} className="px-3 py-1.5 text-xs rounded-lg bg-[#065F46] text-white hover:bg-[#064E3B] flex items-center gap-1.5" data-testid="quick-fix-anomalies-btn">
                      <Zap size={12} /> Auto-Fix Anomalies
                    </button>
                  </div>
                )}
              </div>
              <div className="space-y-3">
                {data.recommendations.map((r, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-[#FAF8F4]">
                    <ArrowRight size={14} className="text-[#C8A961] flex-shrink-0" />
                    <span className="text-sm text-[#1A1A1A]">{r}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {data.recommendations.length === 0 && data.total_score >= 85 && (
            <div className="stratum-card p-8 text-center bg-[#065F46]/5 border-l-4 border-l-[#065F46]">
              <CheckCircle size={32} className="mx-auto text-[#065F46] mb-3" />
              <h3 className="font-display text-lg font-semibold text-[#065F46]">Excellent Compliance</h3>
              <p className="text-sm text-[#6B7280] mt-1">Your asset register is in great shape. Keep up the good work.</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
